import { AvailableTimes } from 'pages/schedules/interface';
import {
  IBadge,
  CertificateSessionCompletionFilter,
  CertificateStatus,
  ICertificate,
  IIssuedCertificate,
  IFrequency,
  IWaitingMedia,
  IEventLocation,
} from 'pages/sessions/interface';
import { IHighlightMedia } from 'pages/articles/interface';
import { ISessionMeta } from '../sessions-create/interface';

export enum EventSessionType {
  GROUP = 'group',
  INDIVIDUAL = 'individual',
}

export interface ISessionLocationParticipant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IModuleSession {
  id: string;
  title: string;
  thumbnailUrl: string;
  sessionType: string;
  audienceType: string;
}

export interface IModuleContent {
  id: string;
  title: string;
  contentUrl: string;
  fileType: string;
  description: string;
}

export interface ISummaryModule {
  name: string;
  session: IModuleSession[];
  form: string[];
  content: IModuleContent[];
  badge: IBadge;
}

export interface ISessionUser {
  id: string,
  avatar: string;
  name: string;
  email?: string;
}

export interface ISessionUpdate {
  user: ISessionUser,
  notification: string;
  date: string;
  isLocationParticipant?: boolean;
}

export interface ISessionSummary {
  user: ISessionUser | null;
  userSessionId:string;
  bookedAt: string;
  completedAt: string;
  isCompleted: boolean;
  isCancel: boolean;
  meetingId?: string;
  slot?: {
    from: string;
    to: string;
  }
  location?: {
    address: string;
    description?: string;
    participants?: ISessionLocationParticipant[];
    participantsCount?: number;
    eventSessionType?: EventSessionType;
  }
  userType: string;
}

export interface IGroupedSessionSummary {
  meetingId: string;
  sessions: ISessionSummary[];
}

export interface ISessionSummaryItem {
  title: string;
  date: string;
  timestamp: number;
  userSessionId?:string;
  slot: {
    from: string;
    to: string;
  } | undefined;
  isCompleted: boolean;
  isCancel: boolean;
  deleted?: boolean;
  meetingId?: string;
  user?: ISessionUser;
  purchases?: ISessionUpdate[];
}

export interface IStatistic {
  allTimeSessionPurchased: number;
  weeklyTimeSessionPurchased: number;
  userEnrolled: number;
  completed: number;
}

export interface ISessionCertificateUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  profileImgUrl?: string;
}

export interface ISessionCertificateUserSession {
  _id: string;
  user: ISessionCertificateUser;
}

export interface ISessionCertificateItem {
  userSession: ISessionCertificateUserSession;
  certificate: IIssuedCertificate | null;
  completion: number;
}

export interface ISessionCertificatesResponse {
  result: ISessionCertificateItem[];
  total: number;
}

export interface IInvitations {
  _id: string;
  userEmail: string;
  type: string;
  resourceId: string;
  status: string;
}

export interface ISession {
  id: string;
  isActive: boolean;
  sessionType: string;
  audienceType: string;
  invitePeople?: string[];
  invitations?: IInvitations[];
  title: string;
  description: string;
  language: string;
  numberOfSessions: number;
  waitingRoomMedia: IWaitingMedia;
  numberOfParticipants: number;
  isObserverAllowed: boolean;
  numberOfObservers: number;
  forms: string[];
  media: string[];
  availableTimes: AvailableTimes[];
  frequency: IFrequency;
  locations?: IEventLocation[];
  fee: string;
  discount: string;
  currency: string;
  subscriberFee: string;
  subscriberCurrency: string;
  subscriberDiscount: string;
  observerFee: string;
  observerDiscount: string;
  observerCurrency: string;
  subscribeObserverFee: string;
  subscribeObserverCurrency: string;
  subscribeObserverDiscount: string;
  summary: ISessionSummary[],
  updates: ISessionUpdate[],
  modules: ISummaryModule[];
  statistics: IStatistic,
  badge: IBadge,
  certificate?: ICertificate | null;
  certificateIssuance?: boolean;
  purchaseHistory: ISessionUpdate[],
  bookedSessions: number;
  bookedObservers: number;
  availableSessions: number;
  availableParticipants: number;
  availableObservers: number;
  sessionMeta?: ISessionMeta;
  deleted: boolean;
  deletedAt: string;
  prerequisites?: { id: string; title: string; }[];
  highlightMedia: IHighlightMedia;
}

export interface SessionSummaryState {
  loading: boolean;
  actionLoading: boolean;
  certificatesStateLoading: boolean;
  certificatesRefetchTrigger: boolean;
  certificatesExportSuccessTrigger: boolean;
  issuingCertificateUserId: string;
  bulkIssuingCertificatesLoading: boolean;
  exportingCertificatesLoading: boolean;
  session: ISession;
  sessionCertificates: ISessionCertificatesResponse;
  sessionUrl: string;
}
export interface ISessionSummaryAction {
  payload: string;
  type:string;
}

export interface IGenerateSession {
  entId: string;
  entUrl: string;
  sessionId: string;
  expertId: string;
  name: string;
  role: string;
  sessionType: string;
  title: string;
  isObserverAllowed: boolean;
  meetingId: string;
  firstName: string;
  lastName: string;
  userId: string;
  profileImgUrl: string;
  email: string;
}
export interface IGenerateSessionStateAction {
  payload: IGenerateSession;
  type: string;
}

export interface ISessionSummeryUpdate {
  id: string;
  audienceType?: string;
  isActive?: boolean;
  invitePeople?: string[];
  resolve?: any;
  reject?: any;
}

export interface ISessionDelete {
  resolve: any;
  reject: any;
  id: string;
  reasonForDeletion: string | null;
  linkedSessionId: string | null | undefined;
}

export interface ISessionInvitationUpdate {
  id: string;
  invitePeople: string[];
}

export interface IGetSessionCertificatesPayload {
  sessionId: string;
  page?: number;
  limit?: number;
  user?: string;
  status?: CertificateStatus;
  issued?: boolean;
  completion?: CertificateSessionCompletionFilter;
}

export interface IExportSessionCertificatesPayload
  extends Omit<IGetSessionCertificatesPayload, 'page' | 'limit'> {}

export interface IIssueSessionCertificatePayload {
  sessionId: string;
  userId: string;
}

export interface IBulkIssueSessionCertificatePayload {
  sessionId: string;
  userIds: string[];
}

export interface IGetSessionCertificatesAction {
  payload: IGetSessionCertificatesPayload;
  type: string;
}

export interface IExportSessionCertificatesAction {
  payload: IExportSessionCertificatesPayload;
  type: string;
}

export interface IIssueSessionCertificateAction {
  payload: IIssueSessionCertificatePayload;
  type: string;
}

export interface IBulkIssueSessionCertificateAction {
  payload: IBulkIssueSessionCertificatePayload;
  type: string;
}

export interface ISessionSummaryUpdateAction {
  payload: ISessionSummeryUpdate;
  type:string;
}

export interface ISessionDeleteAction {
  payload: ISessionDelete;
  type:string;
}

export interface ISessionInvitationUpdateAction {
  payload: ISessionInvitationUpdate;
  type:string;
}

export interface ISupport {
  feedbackType: string;
  feedback: string;
  reference?: string;
  referenceType?: ReferenceType;
}

export interface ISupportAction {
  payload: ISupport;
  type: string;
}

export enum SupportFeedbackTypes {
  bug = 'Bug',
  compaint = 'Complaint',
  improvement = 'Improvement',
}

export enum ReferenceType {
  GENERAL = 'general',
  SESSION = 'session',
}
